/**
 * Armor of God — theme enhancement layer.
 *
 * Progressive enhancement only. Every feature here is additive: if this file
 * fails to load, the storefront still renders and sells normally.
 *
 *   1. Scroll reveal for [data-armor-reveal] elements
 *   2. Sticky-header scrolled state (adds .armor-scrolled to <html>)
 *   3. Marquee speed normalised from content width
 *   4. Recently viewed products (localStorage + ?view=armor-card fetch)
 *
 * Respects prefers-reduced-motion and re-initialises safely when the
 * Theme Editor reloads a section.
 */
(function () {
  'use strict';

  if (window.ArmorReveal) return;

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var STORAGE_KEY = 'armor:recently-viewed';
  var MAX_RECENT = 8;

  /* --------------------------------------------------------- */
  /* 1. Scroll reveal                                           */
  /* --------------------------------------------------------- */

  function reveal(el) {
    el.classList.add('is-visible');
  }

  function initReveal(root) {
    var scope = root || document;
    var elements = scope.querySelectorAll('[data-armor-reveal]:not([data-armor-reveal-bound])');

    if (!elements.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      elements.forEach(function (el) {
        el.setAttribute('data-armor-reveal-bound', 'true');
        reveal(el);
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            reveal(entry.target);
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
    );

    elements.forEach(function (el) {
      el.setAttribute('data-armor-reveal-bound', 'true');
      observer.observe(el);
    });
  }

  /* --------------------------------------------------------- */
  /* 2. Header scroll state                                     */
  /* --------------------------------------------------------- */

  function initHeaderScroll() {
    var root = document.documentElement;
    var ticking = false;

    function update() {
      root.classList.toggle('armor-scrolled', window.scrollY > 24);
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );

    update();
  }

  /* --------------------------------------------------------- */
  /* 3. Marquee speed normalisation                             */
  /* --------------------------------------------------------- */

  function initMarquees(root) {
    var scope = root || document;
    var marquees = scope.querySelectorAll('[data-armor-marquee]:not([data-armor-marquee-bound])');

    marquees.forEach(function (marquee) {
      marquee.setAttribute('data-armor-marquee-bound', 'true');

      var group = marquee.querySelector('.armor-marquee__group');
      if (!group) return;

      // Pixels per second — merchant-configurable via the section setting.
      var speed = parseFloat(marquee.getAttribute('data-armor-marquee-speed')) || 60;

      function apply() {
        var width = group.offsetWidth;
        if (!width) return;
        marquee.style.setProperty('--armor-marquee-duration', (width / speed).toFixed(2) + 's');
      }

      apply();

      if ('ResizeObserver' in window) {
        new ResizeObserver(apply).observe(group);
      } else {
        window.addEventListener('resize', apply, { passive: true });
      }
    });
  }

  /* --------------------------------------------------------- */
  /* 4. Recently viewed                                         */
  /* --------------------------------------------------------- */

  function readRecent() {
    try {
      var parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeRecent(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      /* Private mode or quota exceeded — history simply does not persist. */
    }
  }

  function recordCurrentProduct() {
    var node = document.querySelector('[data-armor-product-handle]');
    if (!node) return;

    var handle = node.getAttribute('data-armor-product-handle');
    if (!handle) return;

    var list = readRecent().filter(function (item) {
      return item !== handle;
    });

    list.unshift(handle);
    writeRecent(list.slice(0, MAX_RECENT));
  }

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function initRecentlyViewed(root) {
    var scope = root || document;
    var containers = scope.querySelectorAll('[data-armor-recently-viewed]:not([data-armor-rv-bound])');

    containers.forEach(function (container) {
      container.setAttribute('data-armor-rv-bound', 'true');

      var grid = container.querySelector('[data-armor-rv-grid]');
      if (!grid || typeof window.fetch !== 'function') return;

      var currentHandle = container.getAttribute('data-armor-current-handle') || '';
      var limit = parseInt(container.getAttribute('data-armor-limit'), 10) || 4;

      var handles = readRecent()
        .filter(function (handle) {
          return handle && handle !== currentHandle;
        })
        .slice(0, limit);

      if (!handles.length) return;

      Promise.all(
        handles.map(function (handle) {
          return fetch(rootUrl() + 'products/' + encodeURIComponent(handle) + '?view=armor-card')
            .then(function (response) {
              return response.ok ? response.text() : '';
            })
            .catch(function () {
              return '';
            });
        })
      ).then(function (chunks) {
        var html = chunks
          .filter(function (chunk) {
            return chunk && chunk.trim();
          })
          .join('');

        if (!html.trim()) return;

        grid.innerHTML = html;
        container.hidden = false;
        initReveal(container);
      });
    });
  }

  /* --------------------------------------------------------- */
  /* Boot                                                       */
  /* --------------------------------------------------------- */

  function init(scope) {
    initReveal(scope);
    initMarquees(scope);
    initRecentlyViewed(scope);
  }

  window.ArmorReveal = { init: init };
  window.ArmorTheme = { init: init };

  function boot() {
    initHeaderScroll();
    recordCurrentProduct();
    init(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
